import { getNodeValidationError, isNodeComplete } from 'modules/workflows/lib/workflowValidation'

import { EdgeUI, NodeUI } from './types'
import { nodeIsValid } from './utils'

// Utility function to update nodes with validation and step calculation
export const updateNodesWithValidationAndSteps = (nodes: NodeUI[], edges: EdgeUI[]): NodeUI[] => {
  const stepByNodeId = new Map<string, number>()
  const triggerNodes = nodes.filter((node) => node.type === 'trigger')

  // Initialize trigger nodes with step 1
  triggerNodes.forEach((trigger) => {
    stepByNodeId.set(trigger.id, 1)
  })

  // Calculate steps using BFS from trigger nodes
  const queue: { nodeId: string; step: number }[] = triggerNodes.map((trigger) => ({
    nodeId: trigger.id,
    step: 1,
  }))
  const visited = new Set<string>(triggerNodes.map((t) => t.id))

  while (queue.length > 0) {
    const node = queue.shift()
    if (!node) continue
    const { nodeId, step } = node

    const outgoingEdges = edges.filter((edge) => edge.source === nodeId)

    outgoingEdges.forEach((edge) => {
      const targetNode = nodes.find((n) => n.id === edge.target)
      if (!targetNode) return

      const shouldIncrementStep =
        targetNode.type !== 'conditionIf' && targetNode.type !== 'conditionElse'
      const newStep = shouldIncrementStep ? step + 1 : step

      const isUnvisited = !visited.has(edge.target)
      const currentStep = stepByNodeId.get(edge.target) ?? Infinity
      const hasLowerStep = currentStep > newStep

      if (isUnvisited || hasLowerStep) {
        stepByNodeId.set(edge.target, newStep)
        visited.add(edge.target)
        queue.push({ nodeId: edge.target, step: newStep })
      }
    })
  }

  // Update nodes with validation and step information
  return nodes.map((node) => {
    const updatedNode = { ...node }
    // Node is valid if it has proper connectivity AND is complete (all required fields filled)
    const hasValidConnectivity = nodeIsValid(node, edges)
    const hasCompleteData = isNodeComplete(node)
    const isActuallyValid = hasValidConnectivity && hasCompleteData

    // Suppress validation in two cases:
    // 1. Node is selected (sidepanel is open) - always suppress while editing
    // 2. Node has suppressInitialValidation flag - prevents flash on newly created nodes
    const isSelected = node.selected === true
    const hasInitialSuppression = node.data?.suppressInitialValidation === true

    // Clear the flag when node becomes selected - this ensures that once the user
    // has interacted with a node (sidepanel opened), the flag is cleared and won't
    // suppress validation when they later deselect the node
    const shouldClearFlag = isSelected && hasInitialSuppression

    const shouldSuppressValidation = isSelected || hasInitialSuppression

    const isValid = shouldSuppressValidation ? true : isActuallyValid

    // Get validation error message for tooltip (only if not suppressing validation)
    const validationError =
      shouldSuppressValidation || isActuallyValid
        ? undefined
        : getNodeValidationError(node) ?? undefined

    updatedNode.data = {
      ...node.data,
      isValid,
      validationError,
      step: stepByNodeId.get(node.id) ?? 1,
      ...(shouldClearFlag ? { suppressInitialValidation: false } : {}),
    }
    return updatedNode
  })
}
